import { useEffect, useRef, useState } from 'react';
import { API_BASE } from '../lib/apiBase';
import { APP_VERSION } from '../lib/appVersion';

/**
 * Was der Nutzer sieht, wenn die Oberfläche abstürzt.
 *
 * Vorher stand hier die Fehlerseite von React Router — „Unexpected Application Error!" mit
 * einem Stack und einem Hinweis an Entwickler. Für den Berater am Kundentisch ist das
 * unbrauchbar: Er weiß nicht, ob seine Arbeit weg ist, und wir erfahren nichts davon.
 *
 * Deshalb: Der technische Bericht geht beim Öffnen von selbst raus (niemand tippt freiwillig
 * einen Stacktrace ab), und daneben steht ein Feld für das, was keine Automatik liefern
 * kann — was der Mensch gerade getan hat.
 *
 * BEWUSST OHNE Router- und Kontext-Abhängigkeiten: Diese Komponente läuft auch dann, wenn
 * genau die kaputt sind. Deshalb auch `fetch` statt des API-Clients — der leitet bei 401
 * um, und eine Umleitung mitten im Absturz verschluckt die Meldung.
 */
export default function FehlerModal({
  fehler,
  componentStack,
  quelle,
}: {
  fehler: unknown;
  componentStack?: string | null;
  quelle: string;
}) {
  const [berichtId, setBerichtId] = useState<number | null>(null);
  const [meldungFehlgeschlagen, setMeldungFehlgeschlagen] = useState(false);
  const [beschreibung, setBeschreibung] = useState('');
  const [gesendet, setGesendet] = useState(false);
  const [sendet, setSendet] = useState(false);
  const [details, setDetails] = useState(false);
  const gemeldet = useRef(false);

  const nachricht =
    fehler instanceof Error
      ? fehler.message
      : typeof fehler === 'string'
        ? fehler
        : ((fehler as { message?: string })?.message ?? 'Unbekannter Fehler');
  const stack = fehler instanceof Error ? (fehler.stack ?? null) : null;

  const kopf = (): Record<string, string> => {
    const h: Record<string, string> = { 'Content-Type': 'application/json' };
    try {
      const token = localStorage.getItem('auth_token');
      if (token) h['Authorization'] = `Bearer ${token}`;
    } catch {
      /* Speicher gesperrt — dann eben anonym */
    }
    return h;
  };

  useEffect(() => {
    // React ruft Effekte im Strict Mode doppelt auf; ohne die Sperre stünde jeder Absturz
    // zweimal in der Ablage.
    if (gemeldet.current) return;
    gemeldet.current = true;
    fetch(`${API_BASE}/api/fehlerberichte`, {
      method: 'POST',
      headers: kopf(),
      body: JSON.stringify({
        url: location.href,
        quelle,
        nachricht,
        stack,
        komponentenStack: componentStack ?? null,
        appVersion: APP_VERSION,
        userAgent: navigator.userAgent,
      }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: { id: number }) => setBerichtId(d.id))
      .catch(() => setMeldungFehlgeschlagen(true));
  }, [nachricht, stack, componentStack, quelle]);

  const senden = async () => {
    if (!beschreibung.trim() || berichtId == null) return;
    setSendet(true);
    try {
      await fetch(`${API_BASE}/api/fehlerberichte/${berichtId}/beschreibung`, {
        method: 'POST',
        headers: kopf(),
        body: JSON.stringify({ beschreibung }),
      });
      setGesendet(true);
    } catch {
      setMeldungFehlgeschlagen(true);
    } finally {
      setSendet(false);
    }
  };

  return (
    <div
      data-testid="fehler-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Ein Fehler ist aufgetreten"
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 p-4 overflow-y-auto"
    >
      <div className="w-full max-w-lg rounded-xl bg-white dark:bg-slate-800 shadow-2xl border border-slate-200 dark:border-slate-700 my-8">
        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center shrink-0">
              <span className="text-xl" aria-hidden="true">⚠️</span>
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                Da ist etwas schiefgelaufen
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                Die Seite konnte nicht angezeigt werden. Ihre gespeicherten Daten sind davon
                nicht betroffen — zuletzt Eingetipptes kann fehlen.
              </p>
            </div>
          </div>

          <p className="mt-4 text-sm text-slate-600 dark:text-slate-400">
            {meldungFehlgeschlagen
              ? 'Die automatische Meldung konnte nicht gesendet werden. Bitte schreiben Sie uns kurz, was passiert ist.'
              : berichtId == null
                ? 'Der Fehler wird an uns gemeldet …'
                : `Der Fehler wurde automatisch an uns gemeldet (Nr. ${berichtId}).`}
          </p>

          {gesendet ? (
            <p className="mt-4 rounded-lg bg-green-50 dark:bg-green-900/20 px-3 py-2 text-sm text-green-800 dark:text-green-300">
              Danke — Ihre Schilderung ist angekommen.
            </p>
          ) : (
            <div className="mt-4">
              <label
                htmlFor="fehler-beschreibung"
                className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
              >
                Was haben Sie gerade gemacht?
              </label>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                Der Hergang hilft uns am meisten: welcher Knopf, welcher Kunde, was Sie erwartet
                haben.
              </p>
              <textarea
                id="fehler-beschreibung"
                rows={3}
                value={beschreibung}
                onChange={(e) => setBeschreibung(e.target.value)}
                placeholder="z. B. „Im Editor auf Kunde suchen geklickt, dann kam der Fehler."
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-slate-400 resize-y"
              />
              <button
                onClick={senden}
                disabled={!beschreibung.trim() || berichtId == null || sendet}
                className="mt-2 px-3 py-1.5 text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white transition-colors"
              >
                {sendet ? 'Wird gesendet …' : 'Absenden'}
              </button>
            </div>
          )}

          <div className="mt-5 flex flex-wrap gap-2">
            <button
              onClick={() => window.location.reload()}
              className="px-3 py-1.5 text-sm font-medium rounded-lg bg-slate-900 dark:bg-slate-200 text-white dark:text-slate-900 hover:opacity-90 transition-opacity"
            >
              Seite neu laden
            </button>
            <button
              onClick={() => { window.location.href = '/'; }}
              className="px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              Zur Übersicht
            </button>
          </div>

          <button
            onClick={() => setDetails((v) => !v)}
            className="mt-4 text-xs text-slate-500 dark:text-slate-400 hover:underline"
          >
            {details ? 'Technische Angaben ausblenden' : 'Technische Angaben anzeigen'}
          </button>
          {details && (
            <pre className="mt-2 max-h-48 overflow-auto rounded-lg bg-slate-100 dark:bg-slate-900/50 p-3 text-[11px] text-slate-600 dark:text-slate-300 whitespace-pre-wrap break-words">
              {`${nachricht}\n\n${stack ?? ''}${componentStack ?? ''}`}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}
